import { http } from '@/plugins/http'
import { normalize, schema } from 'normalizr'
import {
  RECEIVE_CURRENCIES,
  RECEIVING_CURRENCIES,
  RECEIVE_CURRENCY_METADATA,
  RECEIVE_CURRENCY_FAILURE,
  SAVING_CURRENCY,
  SAVING_CURRENCY_FAILURE,
  DELETE_CURRENCY,
  DELETING_CURRENCY,
  DELETING_CURRENCY_FAILURE,
  UPDATING_CURRENCY,
  UPDATING_CURRENCY_FAILURE,
  INCREMENT_CURRENCY_COUNT,
  CLEAR_CURRENCY_STATE,
  RESET_CURRENCY_META,
  CLEAR_STATE
} from '../mutationTypes'
import { checkStatus, getHeaders, parseJSON, buildUrlWithQuery } from '@/utils/api'
import handleXHRError from '@/utils/helpers/HandleXHRError'
import {
  receiveData,
  deleteData,
  increment,
  receiveMetadata,
  resetMetadata,
  clearLocalState,
  clearState
} from '@/store/modules/helpers/mutationHelpers'

const currencySchema = new schema.Entity('currencies')
const currencyListSchema = new schema.Array(currencySchema)

const state = {
  data: {},
  cache: {},
  count: null,
  shown: null,
  next: null,
  previous: null,
  currentPage: 1,
  saving: false,
  saveFailure: null,
  receivingData: false,
  receiveDataFailure: false,
  updating: null,
  updateFailure: null,
  deleting: false,
  deleteFailure: null
}

const actions = {
  fetchCurrenciesWithQuery (store, query) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_CURRENCIES, true)
    return http.get(buildUrlWithQuery('currencies', query, instanceUrl), { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(RECEIVING_CURRENCIES, false)
        this.commit(RESET_CURRENCY_META)
        if (!r.hasOwnProperty('results')) return this.commit(RECEIVE_CURRENCY_FAILURE, 'Error fetching currencies')
        const { results, next, previous, count } = r
        const { entities, result } = normalize(results, currencyListSchema)
        this.commit(RECEIVE_CURRENCIES, { data: entities.currencies })
        this.commit(RECEIVE_CURRENCY_METADATA, { next, previous, count, result: result.length })
        return { query, count, entities }
      })
      .catch(e => {
        this.commit(RECEIVING_CURRENCIES, false)
        this.commit(RECEIVE_CURRENCY_FAILURE, handleXHRError(e))
      })
  }
}

const getters = {
  getCurrencies: (state) => {
    return state.data ? Object.values(state.data).map(v => Object.assign({}, v)) : []
  },
  getAllCurrencies: (state) => {
    return state.cache ? Object.values(state.cache).map(v => Object.assign({}, v)) : []
  },
  getCurrencyMetadata: (state) => {
    const { shown, count } = state
    if (!shown) return { count: 0, shown: 0 }
    return { count, shown }
  },
  getCurrencyById: (state) => (id) => {
    if (state.data && state.data[id]) {
      return state.data[id]
    } else if (state.cache && state.cache[id]) {
      return state.cache[id]
    } else {
      return {}
    }
  },
  getCurrencyByBatchId: (state, rootState) => (batchId) => {
    const bank = rootState.getBankByBatchId(batchId)
    if (!(bank && bank.currency)) return {}
    if (state.data && state.data[bank.currency]) {
      return state.data[bank.currency]
    } else if (state.cache && state.cache[bank.currency]) {
      return state.cache[bank.currency]
    } else {
      return {}
    }
  },
  getCurrencyFetchStatus: (state) => {
    return state.receivingData
  },
  getCurrencySaveStatus: (state) => {
    return state.saving
  },
  getCurrencyUpdateStatus: (state) => {
    return state.updating
  },
  getCurrencyDeleteStatus: (state) => {
    return state.deleting
  }
}

const mutations = {
  [RECEIVE_CURRENCIES] (state, results) {
    receiveData(state, results)
  },
  [RECEIVING_CURRENCIES] (state, payload) {
    state.receivingData = payload
  },
  [RECEIVE_CURRENCY_FAILURE] (state, error) {
    state.receiveDataFailure = error
  },
  [SAVING_CURRENCY] (state, payload) {
    state.saving = payload
  },
  [SAVING_CURRENCY_FAILURE] (state, payload) {
    state.saveFailure = payload
  },
  [UPDATING_CURRENCY] (state, payload) {
    state.updating = payload
  },
  [UPDATING_CURRENCY_FAILURE] (state, payload) {
    const { id, err } = payload
    state.updateFailure = { id, err }
  },
  [DELETE_CURRENCY] (state, payload) {
    deleteData(state, payload)
  },
  [DELETING_CURRENCY] (state, payload) {
    state.deleting = payload
  },
  [DELETING_CURRENCY_FAILURE] (state, payload) {
    const { id, err } = payload
    state.deleteFailure = { id, err }
  },
  [INCREMENT_CURRENCY_COUNT] (state, payload) {
    increment(state, payload)
  },
  [RECEIVE_CURRENCY_METADATA] (state, payload) {
    receiveMetadata(state, payload)
  },
  [CLEAR_CURRENCY_STATE] (state, payload) {
    clearLocalState(state, payload)
  },
  [RESET_CURRENCY_META] (state) {
    resetMetadata(state)
  },
  [CLEAR_STATE] (state) {
    clearState(state)
  }
}

export default {
  state,
  actions,
  getters,
  mutations
}
